// src/pages/public/ConfirmPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../config/firebase";
import {
  addDoc,
  collection,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

/** --- simple hook to detect phone widths --- **/
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

export default function ConfirmPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const hero =
    "https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png";

  // Read everything from router state (set in SelectTimePage)
  const {
    staffId = "",
    staffName = "",
    date = "", // "YYYY-MM-DD"
    start,     // ms since epoch
    services = [],
    totals = { count: 0, totalPrice: 0, totalMin: 0 },
  } = useLocation().state || {};

  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const maxChars = 200;
  const remaining = Math.max(maxChars - note.length, 0);

  // Build display values
  const startDate = useMemo(
    () => (typeof start === "number" ? new Date(start) : null),
    [start]
  );

  const apptWhen = useMemo(() => {
    if (!date || !startDate) return "—";
    const dayStr = new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(startDate);
    const timeStr = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(startDate);
    return `${dayStr}, ${timeStr}`;
  }, [date, startDate]);

  const summary = useMemo(
    () => ({
      total: formatCAD(totals.totalPrice || 0),
      services: totals.count || 0,
      duration: formatDuration(totals.totalMin || 0),
    }),
    [totals]
  );

  const canConfirm =
    staffId !== "" &&
    startDate instanceof Date &&
    isFinite(+startDate) &&
    totals.count > 0;

  const handleConfirm = async () => {
    if (!canConfirm || saving) return;
    try {
      setSaving(true);

      const endDate = new Date(
        +startDate + Number(totals.totalMin || 0) * 60 * 1000
      );

      const docRef = await addDoc(collection(db, "bookings"), {
        staffId,
        staffName: staffName || null,
        date,
        start: Timestamp.fromDate(startDate),
        end: Timestamp.fromDate(endDate),
        services,
        totalPrice: Number(totals.totalPrice || 0),
        totalMin: Number(totals.totalMin || 0),
        note: note.trim() || null,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      navigate("/booking-confirmed", { state: { bookingId: docRef.id } });
    } catch (e) {
      console.error("Save booking failed:", e);
      alert("Sorry, we couldn't save your booking. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!startDate || !totals || !services) {
    return (
      <div style={sx.screen}>
        <div style={sx.containerDesktop}>
          <div style={{ padding: 20 }}>
            Missing booking info. Please re-start your booking.
          </div>
          <button style={sx.backBtn} onClick={() => navigate("/booking")}>
            ← Back to Services
          </button>
        </div>
      </div>
    );
  }

  const heroBoxStyle = isMobile ? sx.heroBoxMobile : sx.heroBoxDesktop;
  const containerStyle = isMobile ? sx.containerMobile : sx.containerDesktop;
  const bottomBarStyle = isMobile ? sx.bottomBarMobile : sx.bottomBarDesktop;

  return (
    <div style={sx.screen}>
      <div style={sx.header} />

      {/* Hero */}
      <div style={heroBoxStyle}>
        <div style={{ ...sx.heroImage, backgroundImage: `url(${hero})` }} />
        <div
          style={{
            ...sx.heroOverlay,
            ...(isMobile ? sx.heroOverlayMobile : {}),
          }}
        >
          <div style={sx.badgeNum}>12</div>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={sx.heroTitle}>613Nail Hub</div>
            <div style={sx.heroMeta}>
              📍 598 Rideau St, Ottawa, ON K1N 6C6 • ☎︎ (613) 327-4049
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={containerStyle}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={sx.pageTitle}>Confirm</div>
        </div>

        {/* Card */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              ...sx.card,
              ...(isMobile ? sx.cardMobile : {}),
            }}
          >
            {/* Salon header */}
            <div style={sx.cardHeader}>
              <div style={sx.salonTitle}>613Nail Hub</div>
              <div style={sx.salonMeta}>
                📍 598 Rideau St, Ottawa, ON K1N 6C6
              </div>
              <div style={sx.salonMeta}>☎︎ (613) 327-4049</div>
            </div>

            {/* Appointment bar */}
            <div style={sx.apptBar}>
              <div style={sx.apptLeft}>
                <span style={sx.apptIcon}>🕘</span>
                <span style={sx.apptText}>{apptWhen}</span>
              </div>
              <div style={sx.apptLeft}>
                <span style={sx.apptIcon}>🧑🏻‍🎨</span>
                <span style={sx.apptText}>
                  {staffName || (staffId === "any" ? "Any staff" : staffId)}
                </span>
              </div>
            </div>

            {/* Services */}
            {services.length === 0 && (
              <div
                style={{ padding: "16px 18px", color: "#6b7280" }}
              >
                No services selected.
              </div>
            )}
            {services.map((s) => (
              <div key={s.id || s.name} style={sx.lineItem}>
                <div>
                  <div style={sx.itemTitle}>{s.name || "Service"}</div>
                  <div style={sx.itemSub}>
                    {formatDuration(s.durationMin || s.duration || 0)}
                  </div>
                </div>
                <div style={sx.priceBlock}>
                  <div style={sx.price}>{formatCAD(Number(s.price || 0))}</div>
                  {s.type?.toLowerCase?.() === "from" && (
                    <div style={sx.from}>From</div>
                  )}
                </div>
              </div>
            ))}

            {/* Estimated total */}
            <div style={sx.totalRow}>
              <div style={{ fontWeight: 700 }}>Estimated Total</div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700 }}>
                  {formatCAD(totals.totalPrice)} + taxes
                </div>
                <div
                  style={{
                    color: "#6b7280",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {summary.services}{" "}
                  {summary.services === 1 ? "service" : "services"} •{" "}
                  {summary.duration}
                </div>
              </div>
            </div>

            {/* Note */}
            <div style={sx.noteWrap}>
              <textarea
                value={note}
                onChange={(e) =>
                  setNote(e.target.value.slice(0, maxChars))
                }
                placeholder="Leave us a note so we can serve you better (optional)"
                style={sx.textarea}
              />
              {/* if you want remaining counter visible, you can render it here */}
              {/* <div style={sx.counter}>{remaining}</div> */}
            </div>
          </div>
        </div>

        {/* Bottom summary + Back/Confirm */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={bottomBarStyle}>
            <div style={sx.summary}>
              <div style={sx.dot} /> <span>{summary.total}</span>
              <div style={{ width: 12 }} />
              <div style={sx.dot} />{" "}
              <span>
                {summary.services}{" "}
                {summary.services === 1 ? "service" : "services"} —{" "}
                {summary.duration}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                ...(isMobile
                  ? { width: "100%", flexDirection: "column-reverse" }
                  : {}),
              }}
            >
              <button
                style={{
                  ...sx.backBtn,
                  ...(isMobile
                    ? { width: "100%", textAlign: "center" }
                    : {}),
                }}
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
              <button
                style={{
                  ...sx.confirmBtn,
                  ...(isMobile ? { width: "100%" } : {}),
                  opacity: canConfirm && !saving ? 1 : 0.7,
                  cursor:
                    canConfirm && !saving ? "pointer" : "not-allowed",
                }}
                disabled={!canConfirm || saving}
                onClick={handleConfirm}
                aria-busy={saving}
              >
                {saving ? "Saving…" : "Confirm →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function formatCAD(n) {
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch {
    return `C$ ${n}`;
  }
}
function formatDuration(min) {
  const m = Number(min) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

/* ---------------- Styles ---------------- */
const sx = {
  screen: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  header: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  // hero wrappers
  heroBoxDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
  },
  heroBoxMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  },

  heroImage: {
    width: "100%",
    height: 210,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    left: 40,
    top: 64,
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "rgba(113,93,89,0.85)",
    padding: "10px 14px",
    borderRadius: 10,
    color: "#fff",
  },
  heroOverlayMobile: {
    left: 16,
    top: 40,
    right: 16,
  },

  badgeNum: { fontSize: 36, fontWeight: 300, lineHeight: 1, padding: "6px 10px" },
  heroTitle: { fontSize: 18, fontWeight: 700 },
  heroMeta: { fontSize: 13, fontWeight: 500, opacity: 0.95 },

  // containers
  containerDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px 40px",
  },
  containerMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "18px 16px 32px",
  },

  pageTitle: {
    fontSize: 37,
    fontWeight: 700,
    color: "#101827",
    margin: "12px 0 16px",
    textAlign: "center",
    width: "100%",
    maxWidth: 820,
  },

  card: {
    width: "100%",
    maxWidth: 820,
    background: "#ffffff",
    borderRadius: 10,
    border: "1px solid #e6e7eb",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  },
  cardMobile: {
    maxWidth: "100%",
    borderRadius: 10,
  },

  cardHeader: { padding: "18px 20px 10px", textAlign: "center" },
  salonTitle: { fontSize: 22, fontWeight: 800, color: "#5c5470" },
  salonMeta: { fontSize: 13, fontWeight: 500, color: "#6b7280", marginTop: 2 },

  apptBar: {
    background: "#5c5470",
    color: "#fff",
    padding: "12px 16px",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "1fr",
  },
  apptLeft: { display: "flex", alignItems: "center", gap: 8 },
  apptIcon: { fontSize: 16 },
  apptText: { fontSize: 14, fontWeight: 700 },

  lineItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 18px",
    borderBottom: "1px solid #f1f2f4",
  },
  itemTitle: { fontSize: 15, fontWeight: 800, color: "#030303", marginBottom: 4 },
  itemSub: { fontSize: 14, color: "#6b7280" },
  priceBlock: { textAlign: "right" },
  price: { fontSize: 15, fontWeight: 700, color: "#030303" },
  from: { fontSize: 12, color: "#6b7280", marginTop: 2 },

  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "18px 18px 6px",
  },

  noteWrap: { position: "relative", padding: "10px 18px 20px" },
  textarea: {
    width: "100%",
    minHeight: 100,
    borderRadius: 8,
    border: "1px solid #ededed",
    outline: "none",
    padding: "12px 12px",
    fontFamily: "inherit",
    fontSize: 14,
    resize: "vertical",
  },
  counter: {
    position: "absolute",
    right: 26,
    bottom: 18,
    background: "#5c5470",
    color: "#fff",
    fontWeight: 700,
    fontSize: 18,
    borderRadius: 8,
    width: 36,
    height: 36,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 0 0 2px #e8ebff inset",
  },

  // bottom bars
  bottomBarDesktop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: 820,
    width: "100%",
    paddingTop: 16,
  },
  bottomBarMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
    maxWidth: 480,
    width: "100%",
    paddingTop: 20,
  },

  summary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 700,
  },
  dot: { width: 6, height: 6, borderRadius: 999, background: "#6b7280" },

  confirmBtn: {
    background: "#5c5470",
    color: "#fff",
    border: 0,
    borderRadius: 22,
    padding: "12px 20px",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
  },
  backBtn: {
    background: "#e7e9ee",
    color: "#334155",
    border: 0,
    borderRadius: 22,
    padding: "12px 18px",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  },
};
